import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const BeforeAfterSlider = ({
  beforeSrc,
  afterSrc,
  beforeLabel = "Before",
  afterLabel = "After",
  aspect = "aspect-[16/10]",
}) => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, p)));
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      updateFromClientX(e.clientX);
    };
    const onUp = () => { draggingRef.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [updateFromClientX]);

  const onMouseDown = (e) => {
    draggingRef.current = true;
    updateFromClientX(e.clientX);
    e.preventDefault();
  };
  const onTouchMove = (e) => {
    if (e.touches[0]) updateFromClientX(e.touches[0].clientX);
  };
  const onTouchStart = (e) => {
    draggingRef.current = true;
    if (e.touches[0]) updateFromClientX(e.touches[0].clientX);
  };

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={() => { draggingRef.current = false; }}
      className={`relative w-full ${aspect} overflow-hidden select-none cursor-ew-resize bg-cream`}
    >
      <img
        src={afterSrc}
        alt={afterLabel}
        draggable={false}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />
      <img
        src={beforeSrc}
        alt={beforeLabel}
        draggable={false}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />

      <div
        className="absolute inset-y-0 w-[2px] bg-white pointer-events-none"
        style={{ left: `${pos}%`, transform: "translateX(-50%)", boxShadow: "0 0 12px rgba(0,0,0,0.35)" }}
      >
        <div
          data-testid="before-after-handle"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white shadow-[0_6px_24px_rgba(0,0,0,0.25)] flex items-center justify-center"
        >
          <ChevronLeft size={18} className="text-navy -mr-1" strokeWidth={1.5} />
          <ChevronRight size={18} className="text-navy -ml-1" strokeWidth={1.5} />
        </div>
      </div>

      <div className="absolute top-4 left-4 md:top-6 md:left-6">
        <span className="inline-block bg-black/65 text-white text-[10px] md:text-xs uppercase tracking-[0.28em] px-3 py-1.5 backdrop-blur-sm">{beforeLabel}</span>
      </div>
      <div className="absolute top-4 right-4 md:top-6 md:right-6">
        <span className="inline-block bg-black/65 text-gold text-[10px] md:text-xs uppercase tracking-[0.28em] px-3 py-1.5 backdrop-blur-sm">{afterLabel}</span>
      </div>
    </div>
  );
};

export default BeforeAfterSlider;
